import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  DialogTitle
} from '@mui/material'
import { DataGrid, GridColDef } from '@mui/x-data-grid'
import { LoadingButton } from '@mui/lab'
import { makeAlertRuleDataGridRows, translateAlertRuleName } from '../utils/alert'
import { createErrorNotification, createSuccessNotification } from '../redux/slices/notifications'
import { useDispatch } from 'react-redux'
import useAlertRules from '../hooks/useAlertRules'
import useDataGrid from '../hooks/useDataGrid'
import { usePolicy } from '../contexts/PolicyContext'
import { Device, Project, RuuviTag } from '../types'
import { AlertGroup, AlertTemplate } from '../types/Alerts'
import AlertRuleLabel from './AlertRuleLabel'
import { AlertRuleDataGridRow } from '../types/AlertRuleDataGridRow'

export interface AlertRuleAddDialogProps extends Omit<DialogProps, 'onClose' | 'children'> {
  alertTemplates: AlertTemplate[]
  alertRules: AlertGroup[]
  project?: Project
  device?: Device
  ruuviTag?: RuuviTag
  useRedux?: boolean
  beforeSubmit?: (input: AlertRuleDataGridRow[]) => void
  afterSubmit?: (alertRules: AlertGroup[]) => void
  onError?: (error: unknown) => void
  onClose: () => void
}

export default function AlertRuleAddDialog({
  alertRules,
  alertTemplates,
  project,
  device,
  ruuviTag,
  useRedux = true,
  beforeSubmit,
  afterSubmit,
  onError,
  onClose,
  open,
  ...props
}: AlertRuleAddDialogProps) {
  /**
   * The translate function.
   */
  const { t } = useTranslation()

  /**
   * The dispatch function.
   */
  const dispatch = useDispatch()

  /**
   * The policy state.
   */
  const { canEditProjectAlertRules, canViewDeviceAlertRules, canEditRuuviTagAlertRules } =
    usePolicy()

  /**
   * Indicates if the user can edit alert rules.
   */
  const canEditAlertRules =
    (project && canEditProjectAlertRules(project)) ||
    (device && canViewDeviceAlertRules(device)) ||
    (ruuviTag && canEditRuuviTagAlertRules(ruuviTag))

  /**
   * Indicates if the alert rules are updating.
   */
  const [isSubmitting, setSubmitting] = useState(false)

  /**
   * The submitting row.
   */
  const [submittingRow, setSubmittingRow] = useState<AlertRuleDataGridRow>()

  /**
   * The pagination model.
   */
  const dataGridProps = useDataGrid()

  /**
   * The disabled alerts as "rows".
   */
  const [rows, setRows] = useState<AlertRuleDataGridRow[]>(() => getInitialRows())

  /**
   * The enabled alerts rules.
   */
  const { updateAlertRules } = useAlertRules({
    project,
    device,
    ruuviTag,
    alertRules,
    alertTemplates,
    useRedux
  })

  /**
   * The columns.
   */
  const columns: GridColDef<AlertRuleDataGridRow>[] = [
    {
      field: 'name',
      headerName: t('name'),
      flex: 1,
      width: 200,
      editable: false,
      valueGetter: (_, row) => translateAlertRuleName(row.name),
      renderCell: ({ row }) => <AlertRuleLabel alertRule={row} />
    }
  ]

  if (canEditAlertRules) {
    columns.push({
      field: 'actions',
      type: 'actions',
      headerName: t('actions'),
      headerAlign: 'center',
      align: 'center',
      hideable: false,
      resizable: false,
      width: 150,
      disableColumnMenu: true,
      renderCell: ({ row }) => (
        <LoadingButton
          type="button"
          variant="outlined"
          size="small"
          color="primary"
          loading={submittingRow?.id === row.id && isSubmitting}
          onClick={() => handleAdd(row)}
        >
          {t('enable')}
        </LoadingButton>
      )
    })
  }

  /**
   * Get the intial rows.
   */
  function getInitialRows(): AlertRuleDataGridRow[] {
    return makeAlertRuleDataGridRows(alertRules, [], false)
  }

  /**
   * Add a new project alert rule.
   */
  async function handleAdd(row: AlertRuleDataGridRow) {
    setSubmittingRow(row)
    await handleUpdateAlertRules([
      {
        ...row,
        enabled: true
      }
    ])
  }

  /**
   * Update the alert rules.
   */
  const handleUpdateAlertRules = async (input: AlertRuleDataGridRow[]) => {
    try {
      setSubmitting(true)
      beforeSubmit?.(input)

      const updatedAlertRules = await updateAlertRules(input)

      setSubmitting(false)
      dispatch(createSuccessNotification(t('enableAlertRuleSuccess')))
      afterSubmit?.(updatedAlertRules)
    } catch (error) {
      console.error(error)
      setSubmitting(false)
      dispatch(createErrorNotification(t('enableAlertRuleError')))
      onError?.(error)
    }
  }

  /**
   * Reset rows when the project alert rules change.
   */
  useEffect(() => {
    setRows(getInitialRows())
  }, [alertRules])

  return (
    <Dialog open={open} maxWidth="md" fullWidth {...props} onClose={onClose}>
      <DialogTitle>{t('addAlertRules')}</DialogTitle>
      <DialogContent>
        {rows.length === 0 ? (
          <Alert severity="info">{t('noAlertRules')}</Alert>
        ) : (
          <DataGrid
            rows={rows}
            columns={columns}
            getRowHeight={() => 72}
            disableColumnMenu
            {...dataGridProps}
          />
        )}
      </DialogContent>
      <DialogActions sx={{ pt: 0 }}>
        <LoadingButton variant="contained" color="primary" loading={isSubmitting} onClick={onClose}>
          {t('done')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  )
}
